import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Popper from '@mui/material/Popper';
import httpCommon from 'src/http-common';
import { useRouter } from 'src/routes/hooks';
import { useAuthContext } from 'src/auth/hooks';
import { storage } from 'src/utils/storage';
import { useSnackbar } from 'src/components/snackbar';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { encryptData } from 'src/sections/auth/jwt/encryptionUtils';

// ----------------------------------------------------------------------

const ModalAutocompletePopper = (props) => (
  <Popper
    {...props}
    placement="bottom-start"
    style={{ ...props.style, zIndex: 2100 }}
  />
);

// ----------------------------------------------------------------------

export default function DepartmentGuard({ children }) {
  const router = useRouter();
  const { logout } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();
  const [modalOpen, setModalOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [deptError, setDeptError] = useState('');
  const [deptOptionsLoading, setDeptOptionsLoading] = useState(false);
  const [deptSaveLoading, setDeptSaveLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [userInfoForUpdate, setUserInfoForUpdate] = useState(null);
  const [siteCodeForDept, setSiteCodeForDept] = useState('');

  useEffect(() => {
    if (!modalOpen || !siteCodeForDept) return undefined;

    const fetchDepartmentOptions = async () => {
      setDeptOptionsLoading(true);
      setDepartmentOptions([]);
      setSelectedDepartment(null);

      try {
        const response = await httpCommon.post('/get_department.php', {
          site_cd: siteCodeForDept,
        });

        if (response?.data?.status === 'SUCCESS') {
          const departments = (response.data.data || []).filter(
            (item) => item.site_cd === siteCodeForDept
          );
          setDepartmentOptions(departments);
        }
      } catch (error) {
        console.error('Error fetching department options:', error);
        enqueueSnackbar('Unable to load department list. Please try again.', {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });
      } finally {
        setDeptOptionsLoading(false);
      }
    };

    fetchDepartmentOptions();
  }, [modalOpen, siteCodeForDept, enqueueSnackbar]);

  const handleDepartmentSave = async () => {
    if (!selectedDepartment) {
      setDeptError('Department is required');
      return;
    }

    setDeptError('');
    setDeptSaveLoading(true);

    try {
      const loginId = storage.get('loginId');

      const response = await httpCommon.post('/update_department.php', {
        emp_mst_login_id: loginId,
        emp_det_department: selectedDepartment,
      });

      if (response.data.status === 'SUCCESS') {
        storage.set('department', encryptData(selectedDepartment));
        window.location.reload();
        return;
      } else {
        enqueueSnackbar(response.data.message || 'Failed to update department.', {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });
      }
    } catch (error) {
      console.error('Error updating department:', error);
      enqueueSnackbar('Failed to update department. Please try again.', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    } finally {
      setDeptSaveLoading(false);
    }
  };

  const handleLogout = async () => {
    setLogoutLoading(true);

    try {
      localStorage.setItem('userLoggedOut', 'true');
      window.dispatchEvent(new CustomEvent('userLogout'));

      await logout();

      localStorage.removeItem('loginId');
      localStorage.removeItem('siteCode');
      localStorage.removeItem('employeeName');
      localStorage.removeItem('activeClient');
      localStorage.removeItem('ldapAuthWw');
      localStorage.removeItem('dateFilter_from');
      localStorage.removeItem('dateFilter_to');
      localStorage.removeItem('firstFrom');

      router.replace('/');
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    } finally {
      setLogoutLoading(false);
      setLogoutConfirmOpen(false);
    }
  };

  useEffect(() => {
    const checkDepartment = async () => {
      const loginId = storage.get('loginId');
      const siteCode = storage.get('siteCode');
      const ldapAuthWw = storage.get('ldapAuthWw');

      // Mandatory department update only applies when LDAP auth is enabled
      if (!loginId || !siteCode || Number(ldapAuthWw) !== 1) return;

      try {
        const response = await httpCommon.post('/GetUserInfo.php', {
          site_cd: siteCode,
          login_id: loginId,
          mst_rowID: 0,
          isCreatingWorkReq: true,
        });

        if (response?.data?.status === 'SUCCESS') {
          const userInfo = response.data.data?.query1_response?.[0];

          if (!userInfo?.emp_det_department) {
            setUserInfoForUpdate(userInfo);
            setSiteCodeForDept(siteCode);
            setModalOpen(true);
            return;
          }

          storage.set('department', encryptData(userInfo.emp_det_department));
        }
      } catch (error) {
        console.error('Error checking user department:', error);
      }
    };

    checkDepartment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {children}

      <Dialog
        open={modalOpen}
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        }}
        fullWidth
        maxWidth="xs"
        sx={{ zIndex: 2000 }}
        PaperProps={{ sx: { overflow: 'visible' } }}
      >
        <DialogTitle>Update Department</DialogTitle>
        <DialogContent sx={{ overflow: 'visible' }}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Your department is not set. Please select your department to continue.
            </Typography>
            <Autocomplete
              value={selectedDepartment}
              onChange={(event, newValue) => {
                setSelectedDepartment(newValue);
                setDeptError('');
              }}
              options={departmentOptions.map((item) => item.dept_mst_dept_cod).filter(Boolean)}
              loading={deptOptionsLoading}
              PopperComponent={ModalAutocompletePopper}
              slotProps={{
                popper: {
                  sx: { zIndex: 2100 },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Department"
                  variant="outlined"
                  error={!!deptError}
                  helperText={deptError}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => setLogoutConfirmOpen(true)}
            sx={{ minHeight: 36, px: 2.5 }}
          >
            Logout
          </Button>
          <LoadingButton
            onClick={handleDepartmentSave}
            loading={deptSaveLoading}
            variant="contained"
            color="success"
            size="small"
            disabled={deptOptionsLoading}
            sx={{ minHeight: 36, px: 2.5 }}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        title="Logout"
        content="You can't proceed without choosing department. So you will be logged out."
        sx={{ zIndex: 2200 }}
        action={
          <LoadingButton
            variant="contained"
            color="error"
            loading={logoutLoading}
            onClick={handleLogout}
          >
            Logout
          </LoadingButton>
        }
      />
    </>
  );
}

DepartmentGuard.propTypes = {
  children: PropTypes.node,
};
